import React from "react";
import {
  ArticleCardCarousel,
  ArticleCardGrid,
  ArticleMasthead,
  ArticleMediaCarousel,
  ArticleMediaEmbed,
  ArticleMediaGallery,
  ArticlePromoCardGrid,
  ArticleRichTextAccordion,
  ArticleRichTextBlade,
  AssetHighlight,
  AudioPlayer,
  BetaCenteredMasthead,
  BetaConnectConsoleMasthead,
  BetaInviteMediaPromo,
  BetaKeyDisplayMasthead,
  BetaRegistrationMasthead,
  BetaSuccessMasthead,
  BetaSurveyMasthead,
  CenteredPromotion,
  CenterPromoMasthead,
  CharacterCardGrid,
  CharacterCardGridMini,
  CharacterChanges,
  CharacterMasthead,
  CharacterMovesList,
  ChooseFighter,
  EmailSubscribe,
  FeaturedNewsCarousel,
  FullWidthImage,
  GameBetaMasthead,
  GameDetailMasthead,
  GameSimpleMasthead,
  GameSpecs,
  IconTab,
  ImageCarousel,
  LandingMediaCarousel,
  LandingMediaEmbed,
  LandingPromoCardCarousel,
  LandingRichTextAccordion,
  LandingRichTextBlade,
  MediaMasthead,
  MediaPromoBlade,
  PatchNotesRichTextBlade,
  PlatformSelectionMasthead,
  ProductRegistration,
  RiftboundCardGallery,
  RiotBar,
  SeparatorBlade,
  SimpleGameCardGrid,
  StructuredPatchNotes,
  TextMasthead,
  TickerBanner,
} from "@riot-private/blades-ui";
import { LooseBlade } from "@riot-private/blades-ui/blades-api-client";
import { metrics } from "@riot-private/pubhub-sdk";
import {
  BladeValidationError,
  formatBladeValidationError,
  parseBladeData,
} from "../utils/api/validate";
import { ErrorBlade } from "./ErrorBlade";

type RenderBladeProps = LooseBlade & {
  index: number;
  locale: string;
  environment: string;
};

function assertNever(_value: never, message: string): never {
  throw new Error(message);
}

function getReportedBladeError(error: Error) {
  if (error instanceof BladeValidationError) {
    return new Error(formatBladeValidationError(error), { cause: error });
  }

  return error;
}

export function RenderBlade({ index, ...bladeProps }: RenderBladeProps) {
  try {
    // Keep the parsed blade intact until after the switch. Destructuring `type`
    // first breaks the discriminated-union narrowing and stops TypeScript from
    // forcing new upstream blade contracts to be handled here.
    const blade = parseBladeData(bladeProps, index);
    const { type, ...bladeData } = blade;

    switch (type) {
      case "articleCardCarousel":
        return <ArticleCardCarousel {...bladeData} />;
      case "articleCardGrid":
        return <ArticleCardGrid {...bladeData} />;
      case "articleMasthead":
        return <ArticleMasthead {...bladeData} />;
      case "articleMediaCarousel":
        return <ArticleMediaCarousel {...bladeData} />;
      case "articleMediaEmbed":
        return <ArticleMediaEmbed {...bladeData} />;
      case "articleMediaGallery":
        return <ArticleMediaGallery {...bladeData} />;
      case "articlePromoCardGrid":
        return <ArticlePromoCardGrid {...bladeData} />;
      case "articleRichTextAccordion":
        return <ArticleRichTextAccordion {...bladeData} />;
      case "assetHighlight":
        return <AssetHighlight {...bladeData} />;
      case "audioPlayer":
        return <AudioPlayer {...bladeData} />;
      case "betaSurveyMasthead":
        return <BetaSurveyMasthead {...bladeData} />;
      case "centeredPromotion":
        return <CenteredPromotion {...bladeData} />;
      case "centerPromoMasthead":
        return <CenterPromoMasthead {...bladeData} />;
      case "characterCardGrid":
        return <CharacterCardGrid {...bladeData} />;
      case "characterCardGridMini":
        return <CharacterCardGridMini {...bladeData} />;
      case "characterChanges":
        return <CharacterChanges {...bladeData} />;
      case "characterMasthead":
        return <CharacterMasthead {...bladeData} />;
      case "characterMovesList":
        return <CharacterMovesList {...bladeData} />;
      case "chooseFighter":
        return <ChooseFighter {...bladeData} />;
      case "emailSubscribe":
        return <EmailSubscribe {...bladeData} />;
      case "featuredNewsCarousel":
        return <FeaturedNewsCarousel {...bladeData} />;
      case "fullWidthImage":
        return <FullWidthImage {...bladeData} />;
      case "gameBetaMasthead":
        return <GameBetaMasthead {...bladeData} />;
      case "gameDetailMasthead":
        return <GameDetailMasthead {...bladeData} />;
      case "gameSimpleMasthead":
        return <GameSimpleMasthead {...bladeData} />;
      case "gameSpecs":
        return <GameSpecs {...bladeData} />;
      case "iconTab":
        return <IconTab {...bladeData} />;
      case "imageCarousel":
        return <ImageCarousel {...bladeData} />;
      case "landingMediaCarousel":
        return <LandingMediaCarousel {...bladeData} />;
      case "landingMediaEmbed":
        return <LandingMediaEmbed {...bladeData} />;
      case "landingPromoCardCarousel":
        return <LandingPromoCardCarousel {...bladeData} />;
      case "landingRichTextAccordion":
        return <LandingRichTextAccordion {...bladeData} />;
      case "mediaPromo":
        return <MediaPromoBlade {...bladeData} />;
      case "articleRichText":
        return <ArticleRichTextBlade {...bladeData} />;
      case "patchNotesRichText":
        return <PatchNotesRichTextBlade {...bladeData} />;
      case "landingRichText":
        return <LandingRichTextBlade {...bladeData} />;
      case "riftboundCardGallery":
        return <RiftboundCardGallery {...bladeData} />;
      case "riotbar":
        return "productId" in bladeData &&
          String(bladeData.productId) !== "false" ? (
          <RiotBar {...bladeData} />
        ) : null;
      case "separator":
        return <SeparatorBlade {...bladeData} />;
      case "simpleGameCardGrid":
        return <SimpleGameCardGrid {...bladeData} />;
      case "textMarquee":
        return <TickerBanner {...bladeData} />;
      case "textMasthead":
        return <TextMasthead {...bladeData} />;
      case "betaCenteredMasthead":
        return <BetaCenteredMasthead {...bladeData} />;
      case "betaConnectConsoleMasthead":
        return <BetaConnectConsoleMasthead {...bladeData} />;
      case "betaInviteMediaPromo":
        return <BetaInviteMediaPromo {...bladeData} />;
      case "betaKeyDisplayMasthead":
        return <BetaKeyDisplayMasthead {...bladeData} />;
      case "betaSuccessMasthead":
        return <BetaSuccessMasthead {...bladeData} />;
      case "betaRegistrationMasthead":
        return <BetaRegistrationMasthead {...bladeData} />;
      case "mediaMasthead":
        return <MediaMasthead {...bladeData} />;
      case "platformSelectionMasthead":
        return <PlatformSelectionMasthead {...bladeData} />;
      case "productRegistration":
        return <ProductRegistration {...bladeData} />;
      case "structuredPatchNotes":
        return <StructuredPatchNotes {...bladeData} />;
      // Blades mapped to existing components with compatible schemas:
      case "spacer":
        return <SeparatorBlade {...bladeData} />;
      case "contentCardGrid":
      case "contentCardGridHero":
        return <ArticleCardGrid {...bladeData} />;
      case "contentCardCarousel":
        return <ArticleCardCarousel {...bladeData} />;
      case "mediaContentCardGrid":
      case "mediaContentCardGridHero":
      case "promoCardsGrid":
      case "rcGameHubPlayerMasthead":
      case "rcGameHubNonPlayerMasthead":
        return <ArticlePromoCardGrid {...bladeData} />;
      case "mediaContentCardCarousel":
        return <LandingPromoCardCarousel {...bladeData} />;
      // Unsupported blade types - Site Builder does not have the extra client
      // data or rendering support needed to handle these safely.
      case "clientNavigation":
      case "gameRegistration":
      case "gameHubPlayerMasthead":
      case "gameHubNonPlayerMasthead":
      case "gameLibrary":
      case "jadeHubMasthead":
      case "leagueClientLinkList":
      case "leagueClientMastheadContentBlock":
      case "leagueClientTabContent":
      case "mediaGallery":
      case "patchNoteCards":
      case "rewardsProgramMasthead":
      case "sTierPromo":
      case "storeCardGrid":
      case "storeProductMasthead":
      case "votingContentBlock":
        return null;
      default: {
        // All cases of Blade types should be defined, so that if any of them are
        // missing TypeScript will catch it. If this starts failing after a
        // blades-ui update, add the new blade type to the switch above instead
        // of weakening the exhaustive check.
        const exhaustiveBlade: never = blade;
        return assertNever(
          exhaustiveBlade,
          `Unhandled blade type in RenderBlade: ${type}`,
        );
      }
    }
  } catch (error) {
    console.error(error);
    // Handle server-side errors with ErrorBlade:
    if (typeof window !== "undefined" && error instanceof Error) {
      metrics.noticeError(getReportedBladeError(error));
    }
    return <ErrorBlade environment={bladeProps.environment} error={error} />;
  }
}
